/**
 * shared.tsx (Inbox) — o que as DUAS telas da Inbox sabem em comum.
 *
 * ─── POR QUE ISTO EXISTE, E O QUE ENTRA AQUI ────────────────────────────────
 * A Inbox passou a ter dois arranjos — `page-completo.tsx` e `page-celular.tsx`
 * —, e ele levantou a pergunta certa antes de eu começar (30/08/2026):
 *
 *   *"Eu não sei se o componente do cartão vai repetir ou não, e se for repetir
 *    a gente tem que seguir a regra e declarar ele como um componente
 *    customizado. Eu acho que eles vão ser diferentes no que vão exibir, mas
 *    vai ser o mesmo cartão."*
 *
 * A resposta, depois de olhar as duas telas: **o conteúdo é o mesmo, o arranjo
 * não.** No desktop a linha é horizontal e cabe tudo; no celular ela empilha e
 * metade some. Um componente único com dois modos de layout vira um emaranhado
 * de `if` que ninguém consegue mudar sem quebrar o outro lado — e a lei do
 * segundo uso existe para evitar duplicação, não para forçar união.
 *
 * Então a divisão é esta, e ela separa pelo RISCO:
 *
 *   AQUI (compartilhado)        o que dói se divergir: como se carrega, o que
 *                              cada pasta contém, como se conta o badge, o que
 *                              é "há 5 dias", que cor tem a borda, o que a
 *                              estrela faz. Se as duas telas calculassem isso
 *                              cada uma do seu jeito, um dia elas discordariam
 *                              — e ninguém descobriria, porque nunca aparecem
 *                              juntas na mesma tela.
 *
 *   NA TELA (cada uma)         o arranjo: que coluna vem antes, o que cabe, o
 *                              que vira segunda linha. Divergir aqui é o ponto.
 *
 *   COMPONENTE `Custom*`       só a peça que é IDÊNTICA nas duas —
 *                              `CustomAvatarSelo`, o rosto com o selo do módulo.
 *
 * ⚠️ NADA DE JSX DE LAYOUT NESTE ARQUIVO. `destacar()` devolve nós React, e é
 * por isso que ele é `.tsx` — mas devolve `<strong>` e `<span>`, não estrutura.
 * No dia em que aparecer uma `<div className="row">` aqui, a divisão acima já
 * terá sido quebrada.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '@/lib/api';
import { formatarData, formatarDiaMes, formatarHora } from '@/lib/data';
import type { Autorizacao } from '@/pages/autorizacoes/shared';

export interface Aviso {
  id: number;
  evento: string | null;
  tipo_aviso: 'recado' | 'pendencia' | 'decisao';
  severidade: 'info' | 'atencao' | 'critico';
  estado: string;
  texto: string;
  link?: string | null;
  lida: boolean;
  favorito: boolean;
  criado_em?: string | null;
  titulo: string;
  icone: string;
  cor: string;
  destaque: boolean;
  origem_nome?: string | null;
  origem_foto?: string | null;
}

export interface Painel {
  decisoes: Aviso[];
  pendencias: Aviso[];
  chegaram: Aviso[];
  contadores: { resolver: number; decisoes: number; chegaram: number };
  nao_lidas: number;
}

const VAZIO: Painel = {
  decisoes: [], pendencias: [], chegaram: [],
  contadores: { resolver: 0, decisoes: 0, chegaram: 0 }, nao_lidas: 0,
};

export type Filtro = 'tudo' | 'resolver' | 'saber' | 'favoritos' | 'arquivados';


/** Quantos itens por página no desktop. O celular rola em vez de paginar. */
export const POR_PAGINA = 15;

/**
 * O ícone do pedido de autorização, num lugar só.
 *
 * ⚠️ NÃO é `bi-patch-question`: a interrogação foi reprovada duas vezes pelo
 * mesmo motivo — *"não dá ideia de autorização"* —, e é verdade, um pedido não
 * é uma pergunta, é um documento parado esperando alguém assinar.
 */
export const ICONE_AUTORIZACAO = 'bi-clipboard-check';

/**
 * O SEGUNDO EIXO — que TIPO de coisa, e não o que se espera dela.
 *
 * Ele pediu depois de aprovar as cinco pastas: *"a gente tem os avisos e a
 * gente tem as autorizações, e eu quero que isso continue separado… a
 * autorização é muito importante e ela merece destaque."*
 *
 * ⚠️ **Isto NÃO virou pasta**, e é a decisão que impede a volta das nove: a
 * pasta responde "o que se espera de você", o tipo responde "que coisa é". São
 * perguntas diferentes, e misturá-las num menu só foi o que produziu
 * `Autorizações › Pendentes` ao lado de `Para resolver` dizendo quase o mesmo.
 * Separadas, elas se COMBINAM: cinco pastas × três tipos são quinze recortes
 * com oito controles, e nenhum nome aparece em dois lugares.
 */
export type Tipo = 'tudo' | 'avisos' | 'autorizacoes';

export const TIPOS: { id: Tipo; rotulo: string; icone: string }[] = [
  { id: 'tudo', rotulo: 'Tudo', icone: 'bi-inbox' },
  { id: 'avisos', rotulo: 'Avisos', icone: 'bi-bell' },
  { id: 'autorizacoes', rotulo: 'Autorizações', icone: ICONE_AUTORIZACAO },
];

/**
 * A COLUNA DE PASTAS. Um grupo por coisa, e o `titulo` vazio é o bloco de cima
 * — `Tudo`, que não pertence a nenhum dos dois porque mostra os dois.
 *
 * O celular usa a MESMA lista, e isso é de propósito: a navegação muda de
 * forma (gaveta em vez de coluna), não de conteúdo. Pasta que exista num
 * aparelho e não no outro é a origem de "no meu celular não tem isso".
 */
/**
 * AS CINCO PASTAS, e o eixo é **o que se espera de você** — a pergunta que a
 * pessoa faz ao abrir a Inbox de manhã.
 *
 * Eram NOVE, em dois grupos, até 30/08/2026. Ele pediu que se parasse e se
 * pensasse — *"esses filtros estão uma zona"* — e o diagnóstico foi o número,
 * não os nomes: quatro pastas tinham zero ou um item, e três pares diziam quase
 * a mesma coisa. Um menu de nove entradas para separar 33 coisas, metade dele
 * não separando nada.
 *
 * O que sumiu, e por quê, está escrito em `servico_avisos.listar_inbox` — junto
 * do código que faz a separação, e não aqui, que é só a lista de rótulos.
 *
 * ⚠️ NÃO HÁ MAIS GRUPOS. O `titulo` continua no tipo porque a gaveta do celular
 * e a coluna do desktop sabem desenhar seções, e um dia pode voltar a haver —
 * mas hoje uma seção só, sem nome, é o certo: cabeçalho para separar cinco
 * itens é hierarquia inventada.
 */
export const GRUPOS: {
  titulo: string;
  entradas: { id: Filtro; rotulo: string; icone: string }[];
}[] = [
  {
    titulo: '',
    entradas: [
      { id: 'tudo', rotulo: 'Tudo', icone: 'bi-inbox' },
      // "Para resolver" leva pendências E pedidos esperando decisão. Um pedido
      // parado é algo para resolver — estavam separados por causa de onde o
      // dado mora, que é razão nossa e não de quem usa.
      { id: 'resolver', rotulo: 'Para resolver', icone: 'bi-exclamation-circle' },
      { id: 'saber', rotulo: 'Só para saber', icone: 'bi-chat-dots' },
      { id: 'favoritos', rotulo: 'Favoritos', icone: 'bi-star' },
      { id: 'arquivados', rotulo: 'Arquivados', icone: 'bi-archive' },
    ],
  },
];

export const TODAS_ENTRADAS = GRUPOS.flatMap((g) => g.entradas);

export const rotuloDoFiltro = (f: Filtro): string =>
  TODAS_ENTRADAS.find((e) => e.id === f)?.rotulo ?? 'Tudo';

export const iconeDoFiltro = (f: Filtro): string =>
  TODAS_ENTRADAS.find((e) => e.id === f)?.icone ?? 'bi-inbox';

/** "29/08 14:32" — e o ano só quando não é este. */
export function quando(iso?: string | null): { dia: string; hora: string } {
  if (!iso) return { dia: '', hora: '' };
  const desteAno = new Date(iso).getFullYear() === new Date().getFullYear();
  return {
    dia: desteAno ? formatarDiaMes(iso) : formatarData(iso),
    hora: formatarHora(iso),
  };
}

/** As iniciais de quem mandou, para quando não há foto. */
export function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

/**
 * A BORDA ESQUERDA DIZ O QUE SE ESPERA DE VOCÊ — decisão, resolver, nada, fim.
 *
 * Não é a origem nem o módulo: numa lista lida de manhã, a pergunta é sempre
 * "o que eu faço primeiro?". Saber que o aviso veio de uma OS não muda a ordem
 * do trabalho; saber que alguém está PARADO esperando, muda.
 *
 * ⚠️ Recado não tem cor, de propósito: se tudo destaca, nada destaca.
 */
export function esperaDe(a: { tipo_aviso: string; estado: string }): string {
  if (a.estado === 'arquivada' || a.estado === 'resolvida') return 'casaa-inbox-espera-fim';
  if (a.tipo_aviso === 'decisao') return 'casaa-inbox-espera-decisao';
  if (a.tipo_aviso === 'pendencia') return 'casaa-inbox-espera-resolver';
  return '';
}

/**
 * HÁ QUANTO TEMPO ISTO ESTÁ PARADO — "há 30 dias", "há 5 dias".
 *
 * ⚠️ Não escreve "sem resposta" nem "prazo", e ele foi explícito na dúvida:
 * "sem resposta" acusa alguém que nem sempre existe; "prazo" promete uma régua
 * que o app ainda não tem. O número cru não mente.
 *
 * O vermelho entra em 7 dias — o único limite já escrito nesta casa.
 */
export function haQuantoTempo(iso?: string | null): { texto: string; tenso: boolean } | null {
  if (!iso) return null;
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias < 1) return null;
  return { texto: dias === 1 ? 'há 1 dia' : `há ${dias} dias`, tenso: dias >= 7 };
}

/**
 * O QUE IMPORTA, EM NEGRITO.
 *
 * A frase do aviso vem pronta do backend, sem campos separados. O que dá para
 * fazer sem inventar contrato novo é reconhecer o que é IDENTIFICADOR: o código
 * de OS e o dinheiro. São os que a pessoa procura varrendo a lista.
 *
 * ⚠️ Nada de `dangerouslySetInnerHTML` — o texto vem do banco e pode conter o
 * que alguém digitou num chat. Isto devolve nós React.
 */
const PADRAO_DESTAQUE = /([A-Z]{2}-\d{4}-\d{4}|R\$\s?[\d.]+,\d{2}|\b\d{1,3}(?:,\d+)?%)/g;

export function destacar(texto: string) {
  return texto.split(PADRAO_DESTAQUE).map((p, i) =>
    PADRAO_DESTAQUE.test(p)
      ? <strong key={i} className="text-body">{p}</strong>
      : <span key={i}>{p}</span>,
  );
}

export type ItemDaLista = { chave: string; data: string; aviso?: Aviso; pedido?: Autorizacao };

/** O que `GET /avisos/lista` devolve. `por_pagina` vem da CONFIGURAÇÃO
 *  (`avisos.itens_por_pagina`), não da tela — quem decide quantos itens cabem é
 *  quem usa, e muda sem deploy. */
interface Pagina {
  itens: { tipo: 'aviso' | 'pedido'; data: string; aviso?: Aviso; pedido?: Autorizacao }[];
  total: number;
  pagina: number;
  por_pagina: number;
  tem_mais: boolean;
}

/**
 * O MOTOR DA INBOX — estado, carga e ações, para os dois arranjos.
 *
 * Tudo o que muda dado ou decide o que a lista contém mora aqui. As telas só
 * escolhem como desenhar o que este hook devolve.
 */
export function usarInbox() {
  const [params, setParams] = useSearchParams();
  const [painel, setPainel] = useState<Painel>(VAZIO);
  const [autorizacoes, setAutorizacoes] = useState<Autorizacao[]>([]);
  const [pag, setPag] = useState<Pagina>({ itens: [], total: 0, pagina: 0, por_pagina: 15, tem_mais: false });
  const [acumulado, setAcumulado] = useState<ItemDaLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  /**
   * A BUSCA QUE VAI AO SERVIDOR É A ADIADA — e a diferença é grande.
   *
   * `busca` acompanha cada tecla (é o que o campo mostra); `buscaFirme` só se
   * mexe depois de 350 ms parado. Sem isso, digitar "Welliton" disparava **24
   * requisições** — oito teclas vezes três chamadas —, e a tela se remontava a
   * cada uma: era essa remontagem que fazia a foto sumir enquanto ele digitava.
   *
   * 350 ms é o intervalo em que a pessoa ainda está digitando a mesma palavra.
   * Mais curto não corta quase nada; mais longo se sente como travamento.
   */
  const [buscaFirme, setBuscaFirme] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaFirme(busca), 350);
    return () => clearTimeout(t);
  }, [busca]);
  const [marcados, setMarcados] = useState<number[]>([]);
  const [recado, setRecado] = useState('');
  const [pagina, setPagina] = useState(0);

  const filtro = (params.get('filtro') || 'tudo') as Filtro;
  const tipo = (params.get('tipo') || 'tudo') as Tipo;

  /** Os dois eixos moram na QUERY STRING — é o que permite mandar por WhatsApp
   *  o link de "as autorizações que esperam você". */
  const mexerNaUrl = (f: Filtro, t: Tipo) => {
    const novo: Record<string, string> = {};
    if (f !== 'tudo') novo.filtro = f;
    if (t !== 'tudo') novo.tipo = t;
    setParams(novo);
  };

  const trocarTipo = (novo: Tipo) => {
    setMarcados([]);
    setRecado('');
    setPagina(0);
    mexerNaUrl(filtro, novo);
  };

  const trocarFiltro = (novo: Filtro) => {
    setMarcados([]); // seleção não atravessa pasta: some da vista, some da conta
    setRecado('');
    setPagina(0);
    mexerNaUrl(novo, tipo);
  };

  /**
   * A LISTA VEM PAGINADA DO SERVIDOR.
   *
   * Antes esta tela pedia tudo e fatiava no navegador — a paginação era
   * cosmética: com 500 avisos, eram 500 linhas de JSON a cada abertura, no 3G
   * da obra, para mostrar quinze.
   *
   * O `painel` continua sendo pedido, mas só para os CONTADORES das pastas:
   * o badge diz quantos há, e isso é uma pergunta sobre o conjunto inteiro, não
   * sobre a página. Ele é leve e já era chamado pelo sino.
   */
  const carregar = useCallback(async (destino = 0, somar = false, silencioso = false) => {
    // ⚠️ O ESQUELETO SÓ APARECE QUANDO NÃO HÁ O QUE MOSTRAR.
    //
    // Ele viu isto em produção e não no local: *"eu clico na estrelinha e os
    // dois itens somem e são redesenhados"*. A causa é esta linha — favoritar
    // chamava `recarregar()`, que ligava `carregando`, e a lista inteira virava
    // placeholder até a resposta voltar. No local isso dura 10 ms e ninguém vê;
    // no VPS dura o bastante para piscar.
    //
    // Trocar a tela por um esqueleto faz sentido na PRIMEIRA carga, quando não
    // há nada. Depois de uma ação pontual, a lista já está certa na tela — o
    // que falta é só confirmar com o servidor, e confirmação não se anuncia.
    if (!somar && !silencioso) setCarregando(true);
    try {
      const q = buscaFirme.trim();
      const busc = q ? `&busca=${encodeURIComponent(q)}` : '';
      // ⚠️ Numa BUSCA, só a lista é refeita. O `painel` e as autorizações
      // alimentam os CONTADORES das pastas, e o badge diz quantos há na pasta —
      // uma pergunta sobre o conjunto inteiro, que a busca não muda. Refazer os
      // três a cada digitação era triplicar o trabalho para o mesmo número.
      const [lista, p, a] = await Promise.all([
        api.get<Pagina>(`/avisos/lista?filtro=${filtro}&tipo=${tipo}&pagina=${destino}${busc}`),
        q ? Promise.resolve(null) : api.get<Painel>('/avisos/painel'),
        q ? Promise.resolve(null) : api.get<Autorizacao[]>('/autorizacoes?minhas=true').catch(() => [] as Autorizacao[]),
      ]);
      const itens: ItemDaLista[] = lista.itens.map((it) => ({
        chave: it.aviso ? `av-${it.aviso.id}` : `au-${it.pedido!.id}`,
        data: it.data, aviso: it.aviso, pedido: it.pedido,
      }));
      setPag(lista);
      setAcumulado((antes) => (somar ? [...antes, ...itens] : itens));
      if (p) setPainel(p);
      if (a) setAutorizacoes(a);
    } catch {
      /* a tela não some por causa de uma falha de rede */
    } finally {
      setCarregando(false);
    }
  }, [filtro, tipo, buscaFirme]);

  // Troca de pasta e busca voltam para a primeira página — continuar na página
  // 3 de uma lista que mudou mostra o meio de outra coisa.
  useEffect(() => { setPagina(0); carregar(0); }, [carregar]);

  /** A página seguinte SUBSTITUI (desktop, setas). */
  const irParaPagina = (n: number) => { setPagina(n); carregar(n); };

  /** A página seguinte SOMA (celular, "Carregar mais"). O polegar já rola, e
   *  uma seta no meio do caminho é um alvo a mais para errar. */
  const carregarMais = () => { const n = pagina + 1; setPagina(n); carregar(n, true); };

  /** Revalida a página atual SEM esqueleto — depois de uma ação pontual, a
   *  lista já está certa; isto só confirma com o servidor. */
  const recarregar = () => carregar(pagina, false, true);

  /**
   * MUDA O ITEM NA LISTA QUE JÁ ESTÁ NA TELA — antes de a resposta voltar.
   *
   * É o que faz o clique parecer instantâneo mesmo com o servidor longe: a
   * estrela acende no mesmo quadro do toque, e o servidor confirma depois. Se a
   * chamada falhar, o `recarregar()` do fim traz a verdade de volta — a tela
   * fica no máximo um instante otimista, nunca mentindo por muito tempo.
   */
  function mexerNoItem(id: number, muda: (a: Aviso) => Aviso) {
    setAcumulado((antes) => antes.map((it) =>
      it.aviso && it.aviso.id === id ? { ...it, aviso: muda(it.aviso) } : it,
    ));
  }

  function tirarDaLista(chave: string) {
    setAcumulado((antes) => antes.filter((it) => it.chave !== chave));
  }

  async function arquivar(a: Aviso) {
    // O cartão sai da lista no ato: arquivar é justamente "sumir daqui", e ver
    // a linha continuar ali por meio segundo faz duvidar se o clique pegou.
    tirarDaLista(`av-${a.id}`);
    try { await api.delete(`/avisos/${a.id}`); } catch { /* ignora */ }
    recarregar();
  }

  async function alternarLido(a: Aviso) {
    mexerNoItem(a.id, (x) => ({ ...x, lida: !a.lida }));
    try { await api.put(`/avisos/${a.id}/lido`, { lida: !a.lida }); } catch { /* ignora */ }
    recarregar();
  }

  async function marcarTudoLido() {
    setAcumulado((antes) => antes.map((it) =>
      it.aviso ? { ...it, aviso: { ...it.aviso, lida: true } } : it,
    ));
    try { await api.post('/avisos/lidos'); } catch { /* ignora */ }
    recarregar();
  }

  /** A ESTRELA. Manda o ESTADO, nunca "alterne" — ver a rota. */
  async function favoritarAviso(a: Aviso) {
    mexerNoItem(a.id, (x) => ({ ...x, favorito: !a.favorito }));
    try { await api.put(`/avisos/${a.id}/favorito`, { favorito: !a.favorito }); } catch { /* ignora */ }
    recarregar();
  }

  async function favoritarPedido(a: Autorizacao) {
    setAcumulado((antes) => antes.map((it) =>
      it.pedido && it.pedido.id === a.id
        ? { ...it, pedido: { ...it.pedido, favorito: !a.favorito } }
        : it,
    ));
    try { await api.put(`/autorizacoes/${a.id}/favorito`, { favorito: !a.favorito }); } catch { /* ignora */ }
    recarregar();
  }

  /** O lote — UMA chamada, e o servidor diz o que não levou. */
  async function arquivarMarcados() {
    try {
      const r = await api.post<{ arquivados: number; ficaram: number }>(
        '/avisos/arquivar', { ids: marcados },
      );
      // Dizer o que FICOU é o ponto: sem isso o lote some com metade da lista e
      // a pessoa conclui que o app engoliu o clique. E a frase concorda.
      const foram = r.arquivados === 1 ? '1 arquivado' : `${r.arquivados} arquivados`;
      const restam = r.ficaram === 1 ? '1 ficou' : `${r.ficaram} ficaram`;
      setRecado(r.ficaram > 0
        ? `${foram}. ${restam}: decisão em aberto ou favorito.`
        : `${foram}.`);
    } catch {
      setRecado('Não deu para arquivar agora.');
    }
    setMarcados([]);
    recarregar();
  }

  async function marcarLidosMarcados() {
    await Promise.all(marcados.map((id) => api.post(`/avisos/${id}/lido`).catch(() => undefined)));
    setMarcados([]);
    recarregar();
  }

  /** Os avisos que existem, para os CONTADORES — não para a lista. */
  const todos = useMemo(
    () => [...painel.decisoes, ...painel.pendencias, ...painel.chegaram]
      .filter((a, i, ls) => ls.findIndex((o) => o.id === a.id) === i)
      .filter((a) => a.tipo_aviso !== 'decisao'),
    [painel],
  );

  const itens = acumulado;

  /** O número ao lado de cada pasta — o que PEDE AÇÃO, não o total.
   *
   * ⚠️ `Tudo` não usa `painel.nao_lidas`: aquele é o número do SININHO e conta
   * outra coisa. Numa coluna, todos contam a mesma coisa. */
  /** O número ao lado de cada pasta — o que PEDE AÇÃO, não o total.
   *
   * ⚠️ `Tudo` não usa `painel.nao_lidas`: aquele é o número do SININHO e conta
   * outra coisa. Numa coluna, todos contam a mesma coisa. */
  function contador(id: Filtro): number {
    const naoLidos = todos.filter((a) => !a.lida);
    const pendentes = autorizacoes.filter((a) => a.status === 'pendente');
    switch (id) {
      case 'tudo': return naoLidos.length + pendentes.length;
      // Soma as duas fontes, igual à pasta: pendência não-lida e pedido parado.
      case 'resolver':
        return naoLidos.filter((a) => a.tipo_aviso === 'pendencia').length + pendentes.length;
      case 'saber': return naoLidos.filter((a) => a.tipo_aviso === 'recado').length;
      // Favoritos mostra o TOTAL, não os não-lidos: o valor da pasta é o que há
      // dentro dela, e o normal é o favorito já ter sido lido.
      case 'favoritos':
        return todos.filter((a) => a.favorito).length + autorizacoes.filter((a) => a.favorito).length;
      // Arquivados não conta: nada ali pede ação, e um badge com 200 é barulho.
      case 'arquivados': return 0;
      default: return 0;
    }
  }

  /** O número de cada aba de tipo — dentro da pasta aberta, não do total.
   *  Uma aba que diz "3" e mostra 3 é a única que não confunde. */
  function contadorTipo(t: Tipo): number {
    const naoLidos = todos.filter((a) => !a.lida);
    const pendentes = autorizacoes.filter((a) => a.status === 'pendente');
    const avisosDaPasta = () => {
      switch (filtro) {
        case 'resolver': return naoLidos.filter((a) => a.tipo_aviso === 'pendencia').length;
        case 'saber': return naoLidos.filter((a) => a.tipo_aviso === 'recado').length;
        case 'favoritos': return todos.filter((a) => a.favorito).length;
        case 'arquivados': return 0;
        default: return naoLidos.length;
      }
    };
    const pedidosDaPasta = () => {
      switch (filtro) {
        case 'resolver': return pendentes.length;
        case 'favoritos': return autorizacoes.filter((a) => a.favorito).length;
        case 'saber':
        case 'arquivados': return 0;
        default: return pendentes.length;
      }
    };
    if (t === 'avisos') return avisosDaPasta();
    if (t === 'autorizacoes') return pedidosDaPasta();
    return avisosDaPasta() + pedidosDaPasta();
  }

  const alternarMarca = (id: number) =>
    setMarcados((ls) => (ls.includes(id) ? ls.filter((x) => x !== id) : [...ls, id]));

  return {
    filtro, trocarFiltro, tipo, trocarTipo, contadorTipo,
    carregando, carregar: recarregar,
    busca, setBusca, recado, setRecado,
    itens, contador, todos, autorizacoes,
    marcados, setMarcados, alternarMarca, temSelecao: marcados.length > 0,
    temNaoLido: todos.some((a) => !a.lida),
    // A paginação agora é do SERVIDOR: a tela não fatia nada.
    pagina, total: pag.total, porPagina: pag.por_pagina, temMais: pag.tem_mais,
    paginas: Math.max(1, Math.ceil(pag.total / (pag.por_pagina || 1))),
    irParaPagina, carregarMais,
    arquivar, alternarLido, marcarTudoLido, favoritarAviso, favoritarPedido,
    arquivarMarcados, marcarLidosMarcados,
  };
}
